import { getAuth } from "firebase/auth";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

async function getAuthHeaders() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) throw new Error("Not authenticated");

  const token = await user.getIdToken();
  
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Appelle /translate (nécessite auth)
 */
export async function translate(text, targetLang, sourceLang = "FR") {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/translate`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      text,
      target_lang: targetLang,
      source_lang: sourceLang,
    }),
  });

  if (!res.ok) throw new Error(`Translate failed (${res.status})`);

  return res.json();
}

/**
 * Appelle /generate-text (nécessite auth)
 */
export async function generateText(prompt, language) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/generate-text`, {
    method: "POST",
    headers,
    body: JSON.stringify({ prompt, language }),
  });

  if (!res.ok) throw new Error(`GenerateText failed (${res.status})`);

  return res.json();
}

/**
 * Appelle /send-email (n'exige pas d'auth)
 */
export async function sendEmailBackend({ name, phone, day, hour, plan }) {
  const res = await fetch(`${API_BASE}/send-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, phone, day, hour, plan }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `SendEmail failed (${res.status})`);
  }

  return res.json();
}

/**
 * Appelle /stripe/create-payment-intent (nécessite auth)
 */
export async function createPaymentIntent(payload) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/create-payment-intent`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Unable to create payment intent");
  }

  return data;
}

/**
 * Crée un compte Stripe Connect Express
 */
export async function createConnectedAccount({ email, country = "FR" }) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/create-connected-account`, {
    method: "POST",
    headers,
    body: JSON.stringify({ email, country }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Impossible de créer le compte Stripe");
  }

  return data;
}

/**
 * Crée un lien d'onboarding Stripe / réouverture Stripe
 */
export async function createOnboardingLink(accountId, restaurantId) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/create-onboarding-link`, {
    method: "POST",
    headers,
    body: JSON.stringify({ accountId, restaurantId }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Impossible d’ouvrir Stripe");
  }

  return data;
}

export async function getStripeAccountStatus(accountId) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/account-status`, {
    method: "POST",
    headers,
    body: JSON.stringify({ accountId }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Impossible de récupérer le statut Stripe");
  }

  return data;
}

export async function createStripeLoginLink(accountId) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/create-login-link`, {
    method: "POST",
    headers,
    body: JSON.stringify({ accountId }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Impossible d’ouvrir Stripe");
  }

  return data;
}

/**
 * Remboursement Stripe total ou partiel
 * amount doit être en centimes si renseigné
 */
export async function refundPayment({
  orderId,
  amount = null,
  reason = "requested_by_customer",
  refundApplicationFee = true,
}) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/stripe/refund-payment`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      orderId,
      amount,
      reason,
      refundApplicationFee,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Impossible d’effectuer le remboursement");
  }

  return data;
}


/**
 * Génère un lien temporaire sécurisé pour télécharger un produit digital acheté
 */
export async function downloadDigitalProduct({ orderId, productId }) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${API_BASE}/download-digital-product`, {
    method: "POST",
    headers,
    body: JSON.stringify({ orderId, productId }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Téléchargement impossible");
  }

  return data;
}